import React, { useState } from 'react';
import { Typography } from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import getDuration from 'utils/getDuration';
import { getStatusStyle } from 'pages/Protected/Backend/LiveActivities/statusStyles';
import MLDialog from 'components/common/Dialog/Dialog';
import CheckIns from '../CheckIns/CheckIns';

const ActivityDetail = ({ activity, colourScheme = 'display' }) => {
  dayjs.extend(utc);
  dayjs.extend(timezone);

  const [showCheckins, setShowCheckins] = useState(false);

  const now = dayjs();
  const startDate = dayjs.utc(activity.ActivityStartDate);
  const endDate = dayjs.utc(activity.ActivityEndDate ?? now);

  const duration = getDuration({
    start: startDate,
    end: endDate,
    expected: activity.ExpectedEndDate,
  });
  const badgeStatus = duration.isOverdue
    ? duration.isLate
      ? 'warning'
      : 'error'
    : 'good';
  const status = getStatusStyle(badgeStatus);

  return (
    <div className="activity-detail w-full">
      <div
        className="w-full rounded-2xl p-5 text-white"
        style={{
          background: `linear-gradient(135deg, ${status.color}, var(--ts-primary-dark))`,
        }}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <span className="rounded-full bg-white/20 px-3 py-1 text-xs font-semibold">
              {activity.ActivityEndDate ? 'Completed' : status.label}
            </span>
            <Typography variant="h4" className="!font-bold mt-2">
              {activity.TrailName}, {activity.ParkName}
            </Typography>
          </div>
          {activity?.CheckIns?.length > 0 && (
            <button
              type="button"
              onClick={() => setShowCheckins(true)}
              className="rounded-full bg-white/15 px-3 py-1.5 text-xs font-medium hover:bg-white/25"
            >
              {activity.CheckIns.length} check-ins
            </button>
          )}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl bg-white/10 px-4 py-3 text-sm">
          <span>
            Started {dayjs.utc(activity.ActivityStartDate).local().format('D MMM, h:mm A')}
          </span>
          <span className="opacity-60">|</span>
          <span>
            {activity.ActivityEndDate ? 'Completed' : 'Expected'}{' '}
            {dayjs
              .utc(activity.ActivityEndDate ?? activity.ExpectedEndDate)
              .local()
              .format('D MMM, h:mm A')}
          </span>
          <span className="opacity-60">|</span>
          <span>{duration.durationText}</span>
          <span className="opacity-60 flex items-center gap-1">
            <PeopleAltIcon sx={{ fontSize: 14 }} />
            {activity.AdditionalParticipants + 1 /* include user */}
          </span>
        </div>

        {duration.isOverdue && (
          <Typography variant="body2" className="mt-3 !font-medium">
            {duration.isLate
              ? `This activity is running late by ${duration.overdueText}`
              : `This activity is overdue by ${duration.overdueText}`}
          </Typography>
        )}
      </div>

      <MLDialog
        openDialog={showCheckins}
        setOpenDialog={setShowCheckins}
        classList={'activity-dashboard-dialog'}
      >
        <CheckIns
          checkins={[...activity.CheckIns]}
          colourScheme="Dashboard"
          dialog={true}
          handleClose={() => setShowCheckins(false)}
        />
      </MLDialog>
    </div>
  );
};

export default ActivityDetail;
